/**
 * Insurance Policies List Page — F007
 *
 * Features:
 * - PI insurance alert banner (info/warning/urgent/critical/blocking)
 * - Filter by insuranceType and policyStatus
 * - "Expiring Soon" toggle (expiringWithin=60 query param)
 * - Row click navigates to detail page
 * - Add Policy modal
 *
 * Schema fields (from actual route/schema — NOT spec):
 *  - insuranceType: pi | public_liability | cyber | management_liability | other
 *  - policyStatus: active | expired | cancelled | pending_renewal (NO 'renewed')
 *  - coverAmount (NOT coverageAmount)
 *  - alertLevel + daysUntilExpiry are calculated fields returned by the API
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Plus, X } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useUrlState } from "@/hooks/use-url-state";
import { apiRequest } from "@/lib/queryClient";
import { ComplianceLayout } from "./compliance-layout";
import { InsurancePolicyModal } from "@/components/modals/insurance-policy-modal";
import { format } from "date-fns";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InsurancePolicy {
  id: string;
  insuranceType: string;
  policyNumber: string | null;
  policyName: string;
  insurerName: string;
  brokerName: string | null;
  coverAmount: string | null;
  excessAmount: string | null;
  coverageSummary: string | null;
  inceptionDate: string | null;
  expiryDate: string | null;
  renewalReminderDate: string | null;
  policyStatus: string;
  premiumAmount: string | null;
  premiumFrequency: string | null;
  policyDocumentUrl: string | null;
  certificateUrl: string | null;
  createdAt: string;
  // Calculated fields from API
  daysUntilExpiry: number | null;
  alertLevel: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLICY_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  active: { label: "Active", className: "bg-green-100 text-green-700 border-green-300" },
  expired: { label: "Expired", className: "bg-red-100 text-red-700 border-red-300" },
  cancelled: { label: "Cancelled", className: "bg-gray-100 text-gray-700 border-gray-300" },
  pending_renewal: { label: "Pending Renewal", className: "bg-amber-100 text-amber-700 border-amber-300" },
};

const INSURANCE_TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  pi: { label: "Prof. Indemnity", className: "bg-blue-100 text-blue-700 border-blue-300" },
  public_liability: { label: "Public Liability", className: "bg-purple-100 text-purple-700 border-purple-300" },
  cyber: { label: "Cyber", className: "bg-teal-100 text-teal-700 border-teal-300" },
  management_liability: { label: "Mgmt Liability", className: "bg-orange-100 text-orange-700 border-orange-300" },
  other: { label: "Other", className: "bg-gray-100 text-gray-700 border-gray-300" },
};

const ALERT_BANNER_STYLES: Record<string, string> = {
  info: "border-blue-300 bg-blue-50 text-blue-800",
  warning: "border-yellow-300 bg-yellow-50 text-yellow-800",
  urgent: "border-orange-300 bg-orange-50 text-orange-800",
  critical: "border-red-300 bg-red-100 text-red-800",
  blocking: "border-red-500 bg-red-200 text-red-900",
};

const ALERT_BANNER_MESSAGES: Record<string, (days: number | null) => string> = {
  info: (days) => `PI Insurance expires in ${days} days`,
  warning: (days) => `PI Insurance expires in ${days} days — action required`,
  urgent: (days) => `PI Insurance expires in ${days} days — urgent action needed`,
  critical: (days) => `PI Insurance expires in ${days} days — broker applications may be blocked`,
  blocking: () => "PI Insurance has expired — application lodgement blocked until renewed",
};

const INSURANCE_TYPE_OPTIONS = [
  { value: "none", label: "All Types" },
  { value: "pi", label: "Professional Indemnity" },
  { value: "public_liability", label: "Public Liability" },
  { value: "cyber", label: "Cyber" },
  { value: "management_liability", label: "Management Liability" },
  { value: "other", label: "Other" },
];

const POLICY_STATUS_OPTIONS = [
  { value: "none", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "pending_renewal", label: "Pending Renewal" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StatusBadgeProps {
  status: string;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const config = POLICY_STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge variant="outline" className={config.className} data-testid={`status-badge-${status}`}>
      {config.label}
    </Badge>
  );
}

interface TypeBadgeProps {
  insuranceType: string;
}

function TypeBadge({ insuranceType }: TypeBadgeProps) {
  const config = INSURANCE_TYPE_CONFIG[insuranceType] ?? {
    label: insuranceType,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Alert Banner
// ---------------------------------------------------------------------------

interface PiAlertBannerProps {
  policies: InsurancePolicy[];
}

function PiAlertBanner({ policies }: PiAlertBannerProps) {
  // Find the PI policy with the most severe alert level
  const SEVERITY_ORDER = ["blocking", "critical", "urgent", "warning", "info"];

  const piPolicies = policies.filter((p) => p.insuranceType === "pi" && p.alertLevel);

  if (piPolicies.length === 0) return null;

  // Pick the most severe
  let mostSevere: InsurancePolicy | null = null;
  for (const level of SEVERITY_ORDER) {
    const match = piPolicies.find((p) => p.alertLevel === level);
    if (match) {
      mostSevere = match;
      break;
    }
  }

  if (!mostSevere || !mostSevere.alertLevel) return null;

  const alertLevel = mostSevere.alertLevel;
  const bannerClass = ALERT_BANNER_STYLES[alertLevel] ?? "";
  const getMessage = ALERT_BANNER_MESSAGES[alertLevel];
  const message = getMessage ? getMessage(mostSevere.daysUntilExpiry) : "";

  return (
    <div
      className={cn("flex items-center gap-2 rounded-lg border p-4", bannerClass)}
      data-testid="pi-alert-banner"
      data-alert-level={alertLevel}
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="text-sm font-medium">{message}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function InsuranceListContent() {
  const [, setLocation] = useLocation();
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);

  const [insuranceType, setInsuranceType] = useUrlState("insuranceType", "none");
  const [policyStatus, setPolicyStatus] = useUrlState("policyStatus", "none");
  const [expiringSoon, setExpiringSoon] = useUrlState<boolean>("expiringSoon", false);

  // Build query params
  const queryParams = new URLSearchParams();
  if (insuranceType !== "none") queryParams.set("insuranceType", insuranceType);
  if (policyStatus !== "none") queryParams.set("policyStatus", policyStatus);
  if (expiringSoon) queryParams.set("expiringWithin", "60");

  const { data, isLoading } = useQuery<InsurancePolicy[]>({
    queryKey: ["/api/compliance/insurance", insuranceType, policyStatus, expiringSoon],
    queryFn: async () => {
      const url = `/api/compliance/insurance${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  const policies = Array.isArray(data) ? data : [];

  const hasActiveFilters =
    insuranceType !== "none" || policyStatus !== "none" || expiringSoon;

  const clearFilters = () => {
    setInsuranceType("none");
    setPolicyStatus("none");
    setExpiringSoon(false);
  };

  const handleRowClick = (policy: InsurancePolicy) => {
    setLocation(`/compliance/insurance/${policy.id}`);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* PI Alert Banner */}
      <PiAlertBanner policies={policies} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Insurance Policies</h1>
          <p className="text-sm text-muted-foreground">
            PI, public liability, cyber and management liability insurance registry
          </p>
        </div>
        <Button
          data-testid="button-add-policy"
          onClick={() => setIsPolicyModalOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Policy
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Insurance Type filter */}
          <Select value={insuranceType} onValueChange={setInsuranceType}>
            <SelectTrigger
              className="w-[200px]"
              data-testid="select-filter-insurance-type"
            >
              <SelectValue placeholder="Insurance Type" />
            </SelectTrigger>
            <SelectContent>
              {INSURANCE_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status filter */}
          <Select value={policyStatus} onValueChange={setPolicyStatus}>
            <SelectTrigger
              className="w-[175px]"
              data-testid="select-filter-status"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {POLICY_STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Expiring Soon toggle */}
          <Button
            variant={expiringSoon ? "default" : "outline"}
            size="sm"
            data-testid="toggle-expiring-soon"
            onClick={() => setExpiringSoon(!expiringSoon)}
          >
            Expiring Soon (60 days)
          </Button>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground"
            >
              <X className="mr-1 h-3 w-3" />
              Clear filters
            </Button>
          )}
        </div>

        {hasActiveFilters && (
          <div className="text-xs text-muted-foreground mt-2">
            Showing {policies.length} polic{policies.length !== 1 ? "ies" : "y"}
          </div>
        )}
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Policy Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Insurer</TableHead>
              <TableHead>Policy Number</TableHead>
              <TableHead>Inception</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : policies.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-muted-foreground"
                >
                  <div className="space-y-2">
                    <p>No insurance policies found</p>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="button-add-policy-empty"
                      onClick={() => setIsPolicyModalOpen(true)}
                    >
                      Add Policy
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              policies.map((policy) => (
                <TableRow
                  key={policy.id}
                  data-testid={`row-policy-${policy.id}`}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(policy)}
                >
                  <TableCell className="font-medium">
                    {policy.policyName}
                  </TableCell>
                  <TableCell>
                    <TypeBadge insuranceType={policy.insuranceType} />
                  </TableCell>
                  <TableCell>{policy.insurerName}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {policy.policyNumber ?? "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(policy.inceptionDate)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(policy.expiryDate)}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={policy.policyStatus} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <InsurancePolicyModal
        open={isPolicyModalOpen}
        onOpenChange={setIsPolicyModalOpen}
        onSuccess={(id) => setLocation(`/compliance/insurance/${id}`)}
      />
    </div>
  );
}

export default function InsurancePage() {
  return (
    <ComplianceLayout>
      <InsuranceListContent />
    </ComplianceLayout>
  );
}
